/**
 * IMPORTS
 */

import { isAxiosError } from "axios";

import {
  ILiberarRemessaPayload,
  ILiberarRemessasLotePayload,
  IRecepcaoLiberarRemessaClientePayload,
} from "@/hooks/liberar-remessa/index.d";

import AxiosService from "@/infra/http/axios";

const handleLoteApiError = (error: unknown) => {
  if (isAxiosError(error) && error.response?.status === 422) {
    return error.response.data;
  }

  throw error;
};

interface ILiberarRemessaRepository {
  liberarRemessa: <ILiberarRemessaResponse>(
    accessToken: string,
    data: ILiberarRemessaPayload
  ) => Promise<ILiberarRemessaResponse>;

  liberarRemessasEmLote: (
    accessToken: string,
    data: ILiberarRemessasLotePayload
  ) => Promise<unknown>;

  recepcaoLiberarRemessaParaCliente: <IRecepcaoLiberarRemessaClienteResponse>(
    accessToken: string,
    data: IRecepcaoLiberarRemessaClientePayload
  ) => Promise<IRecepcaoLiberarRemessaClienteResponse>;

  recepcaoLiberarRemessasEmLote: (
    accessToken: string,
    data: ILiberarRemessasLotePayload
  ) => Promise<unknown>;

  buscarRemessasLiberadasBalcao<T>(
    accessToken: string,
    currentPage: number,
    itemsPerPage: number,
    searchItem: string
  ): Promise<T | null>;
}

const liberarRemessaRepository: ILiberarRemessaRepository = {
  /**
   * Retorna as remessas liberadas para balcao
   * @param accessToken
   * @param currentPage
   * @param itemsPerPage
   * @param searchItem
   * @returns
   */
  async buscarRemessasLiberadasBalcao<T>(
    accessToken: string,
    currentPage: number = 1,
    itemsPerPage: number = 10,
    searchItem = ""
  ): Promise<T | null> {
    const Instance = await AxiosService.createAxiosInstance(accessToken);

    const remessas = await Instance.get(
      `liberar-remessa?page=${currentPage}&per_page=${itemsPerPage}&search=${searchItem}`,
      {
        headers: {
          "content-type": "application/json; charset=UTF-8",
          accept: "*/*",
        },
      }
    );

    return remessas?.data;
  },

  /**
   *  Fazer liberação de uma remessa para o balcao
   * @param accessToken
   * @param payload
   * @returns
   */
  async liberarRemessa(accessToken, payload) {
    const Instance = await AxiosService.createAxiosInstance(accessToken);

    const remessaLiberada = await Instance.post(`liberar-remessa`, payload, {
      headers: {
        "content-type": "application/json; charset=UTF-8",
        accept: "*/*",
      },
    });

    return remessaLiberada?.data;
  },

  async liberarRemessasEmLote(accessToken, payload) {
    try {
      const Instance = await AxiosService.createAxiosInstance(accessToken);

      const response = await Instance.post(`liberar-remessa/lote`, payload, {
        headers: {
          "content-type": "application/json; charset=UTF-8",
          accept: "*/*",
        },
      });

      return response?.data;
    } catch (error) {
      return handleLoteApiError(error);
    }
  },

  /**
   *  Fazer liberação de uma remessa para o cliente
   * @param accessToken
   * @param payload
   * @returns
   */
  async recepcaoLiberarRemessaParaCliente(accessToken, payload) {
    const Instance = await AxiosService.createAxiosInstance(accessToken);

    const recepcaoRemessaLiberada = await Instance.post(`remessa-liberada-cliente`, payload, {
      headers: {
        // "content-type": "application/json; charset=UTF-8",
        accept: "*/*",
      },
    });

    return recepcaoRemessaLiberada?.data;
  },

  async recepcaoLiberarRemessasEmLote(accessToken, payload) {
    try {
      const Instance = await AxiosService.createAxiosInstance(accessToken);

      const response = await Instance.post(`remessa-liberada-cliente/lote`, payload, {
        headers: {
          "content-type": "application/json; charset=UTF-8",
          accept: "*/*",
        },
      });

      return response?.data;
    } catch (error) {
      return handleLoteApiError(error);
    }
  },
};

/**
 * EXPORTS
 */
export { liberarRemessaRepository };
